import { useCallback, useEffect, useMemo, useState } from "react";

import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";

import config from "console_config";
import { ACTIVITY_TYPES } from "Constants/constants";
import useDecodedParams from "Hooks/useDecodedParams";
import getUrl from "Libs/getUrl";
import logger from "Libs/logger";
import client, { request } from "Libs/platform";
import {
  meSelector,
  selectCurrentUserIsInternal
} from "Reducers/app/selectors";
import { useAppSelector } from "Store/hooks";

import {
  FormatedGithubOrganization,
  GithubOrganization,
  GithubRepositories,
  GithubBranch,
  SetupStepperContextType,
  VerificationTypes,
  errorTypes,
  onboardingTypes,
  projectCreationStates,
  verificationTypes,
  FormatedGithubRepository,
  OnboardingTypes
} from "./types";

import type { Activity } from "@packages/client";

const githubAppEndpoint = `${config.URL_API_PROXY}/gh`;

export function useGithubApp() {
  const [githubOrganizations, setGithubOrganizations] =
    useState<GithubOrganization[]>();
  const [selectedGithubOrganization, setSelectedGithubOrganization] =
    useState<FormatedGithubOrganization | null>(null);
  const [githubRepositories, setGithubRepositories] =
    useState<GithubRepositories>();
  const [selectedGithubRepository, setSelectedGithubRepository] =
    useState<FormatedGithubRepository | null>(null);
  const [githubRepositoriesBranches, setGithubRepositoriesBranches] =
    useState<GithubBranch[]>();
  const [selectedGithubRepositoryBranch, setSelectedGithubRepositoryBranch] =
    useState<{ label: string; value: string } | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>();

  const installGithubApp = ({
    organizationId,
    organizationName
  }: {
    organizationId: string;
    organizationName: string;
  }) => {
    const redirectOnSuccessURL = `${window.location.origin}${getUrl({
      key: "organization.createProject.repository",
      props: {
        organizationId: organizationName
      }
    })}`;
    const redirectOnErrorURL = `${window.location.origin}${getUrl({
      key: "organization.createProject",
      props: {
        organizationId: organizationName
      }
    })}`;

    // This is a fixed value always
    window.location.href = `${config.URL_SIA}/gh/${organizationId}/install?next=${redirectOnSuccessURL}&error_next=${redirectOnErrorURL}`;
  };

  const getGithubOrganizations = async ({
    organizationId
  }: {
    organizationId: string;
  }) => {
    setIsLoading(true);
    try {
      const githubOrganizations = (await request(
        `${githubAppEndpoint}/${organizationId}/orgs`
      )) as GithubOrganization[];
      setGithubOrganizations(githubOrganizations);
      return githubOrganizations;
    } catch (error) {
      // todo: redirect to the beggining of the onboarding flow if it fails
      // to fetch
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError("An error occured");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getGithubRepositories = async ({
    organizationId,
    installationId
  }: {
    organizationId: string;
    installationId: string;
  }) => {
    setIsLoading(true);
    try {
      const githubOrganizationRepositories = (await request(
        `${githubAppEndpoint}/${organizationId}/orgs/${installationId}/repos`
      )) as GithubRepositories;
      setGithubRepositories(githubOrganizationRepositories);
      return githubOrganizationRepositories;
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError("An error occured");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getGithubRepositoryBranches = async ({
    selectedRepository,
    organizationId,
    installationId
  }: {
    selectedRepository: string;
    organizationId: string;
    installationId: string;
  }) => {
    setIsLoading(true);
    try {
      const githubRepositoryBranches = await request(
        `${githubAppEndpoint}/${organizationId}/orgs/${installationId}/repos/${selectedRepository}/branches`
      );

      setGithubRepositoriesBranches(githubRepositoryBranches);
      return githubRepositoryBranches;
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError("An error occured");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchGithubApp = async ({
    organizationId
  }: {
    organizationId: string;
  }) => {
    const githubOrganizations = await getGithubOrganizations({
      organizationId
    });
    const selectedGithubOrganizationInformation = githubOrganizations?.find(
      githubOrganization =>
        githubOrganization.id === selectedGithubOrganization?.value
    );

    if (error || !selectedGithubOrganizationInformation) {
      return;
    }
    await getGithubRepositories({
      organizationId: selectedGithubOrganizationInformation.organization_id,
      installationId: selectedGithubOrganizationInformation?.id
    });
  };

  function handleChangeGithubOrganization(
    newSelectedGithubOrganization: FormatedGithubOrganization | null
  ) {
    if (
      newSelectedGithubOrganization?.value === selectedGithubOrganization?.value
    ) {
      return;
    }
    setSelectedGithubOrganization(newSelectedGithubOrganization);
    setSelectedGithubRepository(null);
    setGithubRepositories(undefined);
    const selectedGithubOrganizationInformation = githubOrganizations?.find(
      githubOrganization =>
        githubOrganization.id === newSelectedGithubOrganization?.value
    );
    if (!selectedGithubOrganizationInformation) {
      return;
    }
    getGithubRepositories({
      organizationId: selectedGithubOrganizationInformation.organization_id,
      installationId: selectedGithubOrganizationInformation.id
    });
  }
  const handleChangeGithubRepository = ({
    newSelectedGithubRepository,
    installationId,
    organizationId
  }: {
    newSelectedGithubRepository: FormatedGithubRepository | null;
    installationId: any;
    organizationId: any;
  }) => {
    if (
      selectedGithubRepository?.value === newSelectedGithubRepository?.value
    ) {
      return;
    }
    setSelectedGithubRepository(newSelectedGithubRepository);

    setSelectedGithubRepositoryBranch(null);

    const selectedGithubRepositoryInformation =
      githubRepositories?.repositories?.find(
        githubRepository =>
          githubRepository.name === newSelectedGithubRepository?.value
      );
    if (!selectedGithubRepositoryInformation) {
      return;
    }
    getGithubRepositoryBranches({
      selectedRepository: selectedGithubRepositoryInformation?.name,
      organizationId,
      installationId
    });
  };

  const createProjectFromGithub = async (
    data: SetupStepperContextType["setupStepperValues"]
  ) => {
    setIsLoading(true);
    localStorage.setItem("installationId", data.installationId!);
    localStorage.setItem("repositoryName", data.repository?.value!);
    try {
      const subscription = await request(
        `${githubAppEndpoint}/${data.organization.value}/orgs/${data.installationId}/repos/${data.repository?.value}`,
        "POST",
        {
          project_region: data.region.id,
          project_title: data.projectName,
          default_branch: data.productionEnvironment
        }
      );
      return subscription;
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError("An error occured");
      }
      return {};
    } finally {
      setIsLoading(false);
    }
  };

  return {
    installGithubApp,
    fetchGithubApp,
    getGithubRepositoryBranches,
    getGithubRepositories,
    githubOrganizations,
    githubRepositories,
    createProjectFromGithub,
    selectedGithubOrganization,
    selectedGithubRepository,
    handleChangeGithubOrganization,
    setSelectedGithubRepository,
    selectedGithubRepositoryBranch,
    setSelectedGithubRepositoryBranch,
    handleChangeGithubRepository,
    githubRepositoriesBranches,
    isLoading,
    error
  };
}

export function useVerification(organizationId: string) {
  const isInternalUser = useAppSelector(selectCurrentUserIsInternal);
  const navigate = useNavigate();
  const location = useLocation();

  const [isOnTrial, setIsOnTrial] = useState(false);

  const getVerificationType = useCallback(
    async (options?: { isRefreshing?: boolean }) => {
      if (!organizationId) return { type: "idle", credentials: {} };
      try {
        if (!options?.isRefreshing) {
          const organizationProfile =
            await client.getOrganizationProfile(organizationId);
          const isOnTrial =
            Object.keys(organizationProfile ?? {}).length !== 0 &&
            organizationProfile?.current_trial?.active;
          setIsOnTrial(isOnTrial);
        }
        const { required_action, can_create } =
          await client.getOrganizationSubscriptionCanCreate(organizationId);

        /**
         * If they are PSH user, don't show screen
         * If they are on trial, and they have a payment method, don't show screen
         * If they are on trial, and they don't have a payment method, show credit card
         * If they aren't on trial, and they have a payment method that is chargable, don't show screen
         * If they aren't on trial, and they don't have a payment method or is not chargable, show full billing
         */

        const shouldSkipRedirection = location.pathname.includes("/setup/");
        if (isInternalUser) {
          return { type: "none", credentials: required_action?.credentials };
        }
        if (required_action?.action === "billing_details") {
          return { type: "full", credentials: required_action?.credentials };
        }
        if (required_action?.type === "credit-card") {
          return {
            type: "credit-card",
            credentials: required_action?.credentials
          };
        }
        if (required_action?.action === "retry") {
          return { type: "idle", credentials: required_action?.credentials };
        }
        if (!can_create && !shouldSkipRedirection) {
          navigate("/");
        }

        return { type: "none", credentials: required_action?.credentials };
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        return { type: "idle", credentials: {} };
      }
    },
    [isInternalUser, location.pathname, navigate, organizationId]
  );

  return {
    getVerificationType,
    isOnTrial
  };
}

export function useCreateProject() {
  const { organizationId: organizationName } = useDecodedParams<{
    organizationId: string;
  }>();
  const [newProjectValues, setNewProjectValues] = useState<
    SetupStepperContextType["newProjectValues"]
  >({
    errorType: errorTypes.default,
    errorDetail: undefined,
    projectId: undefined,
    environmentId: undefined,
    projectUrl: undefined,
    publicUrl: undefined,
    state: projectCreationStates.isBeingCreated
  });
  const intl = useIntl();
  const [isCreateProjectLoading, setIsCreateProjectLoading] = useState(false);
  const [activeProjectSubscription, setProjectSubscription] =
    useState<EventSource>();
  const navigate = useNavigate();
  const [activities, setActivities] = useState<Activity[]>([]);

  const { createProjectFromGithub } = useGithubApp();

  // This function is needed because when a client request 400's it throws
  // an error and gives you the response as a stringified JSON
  const safeClient = async <T extends object>(
    callback: () => Promise<T>
  ): Promise<
    | { success: true; response: Awaited<T> }
    | {
        success: false;
        error: {
          status?: number;
          detail?: string;
          message?: string;
          title?: string;
        };
      }
  > => {
    try {
      const response = await callback();

      if (
        "status" in response &&
        [400, 403, 404].includes(response.status as number)
      ) {
        throw new Error((response as unknown as { detail: string }).detail);
      }
      return { success: true, response };
    } catch (error) {
      if (error instanceof Error) {
        setNewProjectValues(currentState => ({
          ...currentState,
          errorType: errorTypes.custom,
          errorDetail: error.message
        }));
      }
      let parsedError = error as {
        status?: number;
        detail?: string;
        message?: string;
        title?: string;
      };

      if (!(parsedError.title || parsedError.detail || parsedError.message)) {
        try {
          parsedError = JSON.parse((error as string) ?? "");
        } catch (error) {
          // If it catches then it means is not a JSON
          parsedError = { detail: error as string };
        }
      }

      setNewProjectValues(currentState => ({
        ...currentState,
        errorType: errorTypes.custom,
        errorDetail:
          (parsedError as { title: string }).title ||
          (parsedError as { detail: string }).detail ||
          (parsedError as { message: string }).message
      }));
      return { success: false, error: parsedError };
    }
  };

  const getSubscription = useCallback(
    async ({
      organizationId,
      projectId
    }: {
      organizationId: string;
      projectId: string;
    }) => {
      return await safeClient(
        async () =>
          await client.getOrganizationSubscription(organizationId, projectId)
      ).then(res => (res.success ? res.response : undefined));
    },
    []
  );

  const getProject = useCallback(async (projectId: string) => {
    return await safeClient(
      async () => await client.getProject(projectId)
    ).then(res => (res.success ? res.response : undefined));
  }, []);

  const getActivities = useCallback(
    async (projectId: string, onboardingType: OnboardingTypes) => {
      const activities = await safeClient(
        async () =>
          await client.getProjectActivities(projectId, [
            ACTIVITY_TYPES.ENVIRONMENT.PUSH,
            ACTIVITY_TYPES.ENVIRONMENT.ACTIVATE,
            ACTIVITY_TYPES.ENVIRONMENT.INITIALIZE,
            ACTIVITY_TYPES.PROJECT.CREATE
          ])
      ).then(res => (res.success ? res.response : undefined));

      if (!activities) return;

      setActivities(
        activities.reverse().filter(activity => {
          // This are the activities we will show on the build log
          // In this case for the github flow we want to only show non main active environment activities
          // And for the rest of the flows only main

          if (!activity?.payload?.environment) {
            return true;
          }

          if (onboardingType === onboardingTypes.migrate) {
            return (
              activity?.payload?.environment?.status === "active" &&
              !activity?.payload?.environment?.is_main
            );
          }

          return true;
        })
      );
      return activities;
    },
    []
  );

  const getDefaultEnvironmentId = useCallback(
    async (projectId: string, projectDefaultBranch: string) => {
      return await safeClient(
        async () => await client.getEnvironment(projectId, projectDefaultBranch)
      ).then(res => {
        return res.success ? res.response.id : "";
      });
    },
    []
  );

  const getCurrentDeployment = useCallback(
    async (
      projectId: string,
      projectDefaultBranch: string,
      options?: {
        environmentId?: string;
      }
    ) => {
      const environmentId =
        options?.environmentId ??
        (await getDefaultEnvironmentId(projectId, projectDefaultBranch));
      const encodedEnvId = encodeURIComponent(environmentId);
      const currentDeployment = await client
        .getCurrentDeployment(projectId, encodedEnvId, {
          verbose: true
        })
        // We allow this to fail because we rely on the response code
        // to know the reason why a deployment failed
        .catch(error => error);

      return currentDeployment;
    },
    [getDefaultEnvironmentId]
  );

  const getPrimaryURL = useCallback(
    async (projectId: string, projectDefaultBranch: string) => {
      const currentDeployment = await getCurrentDeployment(
        projectId,
        projectDefaultBranch
      );
      const routes = currentDeployment?.routes;
      let primaryRoute = "";

      if (routes) {
        primaryRoute =
          Object.keys(routes).find(route => routes[route].primary === true) ||
          Object.keys(routes).find(
            route => routes[route].type !== "redirect"
          ) ||
          Object.keys(routes)[0];
      }

      return primaryRoute;
    },
    [getCurrentDeployment]
  );

  const setActivityListener = useCallback(
    ({
      projectSubscription,
      callback
    }: {
      projectSubscription: EventSource;
      callback: (...args: unknown[]) => unknown;
    }) => {
      projectSubscription?.removeEventListener("resource/activity", callback);
      projectSubscription?.addEventListener("resource/activity", callback);
    },
    []
  );

  const checkPushActivity = useCallback(
    ({
      projectId,
      projectDefaultBranch,
      onFinish,
      onboardingType
    }: {
      projectId: string;
      projectSubscription: EventSource;
      projectDefaultBranch: string;
      onFinish: () => void;
      onboardingType: OnboardingTypes;
    }) =>
      async message => {
        if (!message.data) {
          return false;
        }
        const activity = JSON.parse(message.data);

        // Here we are checking that the activity is either push or activate
        // And then we check if it's on the github flow, if so we only take into consideration
        // active non main environments, if not we only take into consideration main environments
        const isEnvironmentPushOrActivate =
          activity?.data?.type === ACTIVITY_TYPES.ENVIRONMENT.PUSH ||
          activity?.data?.type === ACTIVITY_TYPES.ENVIRONMENT.INITIALIZE ||
          activity?.data?.type === ACTIVITY_TYPES.ENVIRONMENT.ACTIVATE;

        const isDeploymentRelatedEnvironment =
          onboardingType === onboardingTypes.migrate
            ? activity?.data?.payload?.environment?.status === "active" &&
              !activity?.data?.payload?.environment?.is_main
            : true;

        if (isEnvironmentPushOrActivate && isDeploymentRelatedEnvironment) {
          getActivities(projectId, onboardingType);

          if (
            activity.data.state === "complete" &&
            activity.data.result === "success"
          ) {
            const primaryRoute = await getPrimaryURL(
              projectId,
              activity.data.parameters.environment ?? projectDefaultBranch
            );
            setNewProjectValues(currentState => ({
              ...currentState,
              publicUrl: primaryRoute,
              errorType: errorTypes.default,
              environmentId:
                activity.data.parameters.environment ?? projectDefaultBranch,
              state: projectCreationStates.finished
            }));
            return onFinish();
          }
          if (
            activity.data.state === "cancelled" ||
            activity.data.result === "failure"
          ) {
            setNewProjectValues(currentState => ({
              ...currentState,
              errorType: errorTypes.deployment,
              state: projectCreationStates.isWaitingForPush
            }));
            return;
          }
          if (
            activity.data.state === "pending" ||
            activity.data.state === "in_progress"
          ) {
            setNewProjectValues(currentState => ({
              ...currentState,
              state: projectCreationStates.isDeploying
            }));
            return;
          }
        }
      },
    [getPrimaryURL, getActivities]
  );

  /**
   * If the flow is scratch, check if there is a push activity
   * 1. Yes, it was successful [Continue]
   * 2. Yes, it was a failure [Status: Error & Waiting for push activity]
   * 3. Yes, it is on going (store id and wait for it to finish)
   * 4. No, subscribe to the project and wait for a push activity [Status: Waiting for push]
   */
  const checkPush = useCallback(
    async ({
      lastPushActivity,
      projectId,
      projectSubscription,
      projectDefaultBranch,
      onFinish,
      onboardingType
    }: {
      lastPushActivity: Activity | undefined;
      projectId: string;
      projectDefaultBranch: string;
      projectSubscription: EventSource;
      onFinish: () => void;
      onboardingType: OnboardingTypes;
    }) => {
      if (
        lastPushActivity?.state === "complete" &&
        lastPushActivity?.result === "success"
      ) {
        const primaryRoute = await getPrimaryURL(
          projectId,
          (lastPushActivity.parameters.environment as string) ??
            projectDefaultBranch
        );
        setNewProjectValues(currentState => ({
          ...currentState,
          publicUrl: primaryRoute,
          environmentId:
            (lastPushActivity.parameters.environment as string) ??
            projectDefaultBranch,
          errorType: errorTypes.default,
          state: projectCreationStates.finished
        }));
        return onFinish();
      }
      if (
        lastPushActivity?.state === "cancelled" ||
        lastPushActivity?.result === "failure"
      ) {
        setActivityListener({
          projectSubscription,
          callback: checkPushActivity({
            projectId,
            projectSubscription,
            projectDefaultBranch,
            onFinish,
            onboardingType
          })
        });
        throw new Error(errorTypes.deployment);
      }
      if (
        lastPushActivity?.state === "in_progress" ||
        lastPushActivity?.state === "pending"
      ) {
        setNewProjectValues(currentState => ({
          ...currentState,
          state: projectCreationStates.isDeploying
        }));
        setActivityListener({
          projectSubscription,
          callback: checkPushActivity({
            projectId,
            projectSubscription,
            projectDefaultBranch,
            onFinish,
            onboardingType
          })
        });
        return;
      }
      if (!lastPushActivity) {
        setActivityListener({
          projectSubscription,
          callback: checkPushActivity({
            projectId,
            projectSubscription,
            projectDefaultBranch,
            onFinish,
            onboardingType
          })
        });
        return;
      }
    },
    [checkPushActivity, getPrimaryURL, setActivityListener]
  );

  const [hasStartedChecking, setHasStartedChecking] = useState(false);

  const checkSubscriptionStatus = useCallback(
    ({
      projectId,
      organizationId,
      onboardingType
    }: {
      projectId: string;
      organizationId: string;
      onboardingType: OnboardingTypes;
    }) => {
      const checkSubscriptionIntervalCallback = async ({
        onFinish
      }: {
        onFinish: () => void;
      }) => {
        if (hasStartedChecking) return;
        setHasStartedChecking(true);
        const subscription = await getSubscription({
          organizationId,
          projectId
        });

        if (!subscription) return;

        const projectOverviewUrl = getUrl({
          key: "organization.project",
          props: {
            organizationId: organizationName,
            projectId: subscription?.project_id ?? ""
          }
        });

        try {
          if (subscription.status === "provisioning failure") {
            onFinish();
            throw new Error(errorTypes.provisioningFailure);
          }
          if (subscription.status === "active") {
            onFinish();
            setNewProjectValues(currentState => ({
              ...currentState,
              projectId: subscription.project_id,
              projectUrl: projectOverviewUrl,
              state: projectCreationStates.isWaitingForPush
            }));

            const project = await getProject(subscription.project_id);

            if (!project) return;

            let projectSubscription = activeProjectSubscription;
            if (!projectSubscription) {
              const activeProjectSubscription = await project.subscribe();
              setProjectSubscription(activeProjectSubscription);
              projectSubscription = activeProjectSubscription;
            }
            const activities = await getActivities(
              subscription.project_id,
              onboardingType
            );
            const lastPushActivity =
              activities?.find(
                activity => activity.type === ACTIVITY_TYPES.ENVIRONMENT.PUSH
              ) ||
              activities?.find(
                activity =>
                  activity.type === ACTIVITY_TYPES.ENVIRONMENT.ACTIVATE
              ) ||
              activities?.find(
                activity =>
                  activity.type === ACTIVITY_TYPES.ENVIRONMENT.INITIALIZE
              );

            setNewProjectValues(currentState => ({
              ...currentState,
              logUrl: lastPushActivity?.id
                ? getUrl({
                    key: "organization.project.log",
                    props: {
                      organizationId: organizationName,
                      projectId: subscription?.project_id,
                      activityId: lastPushActivity?.id || ""
                    }
                  })
                : projectOverviewUrl
            }));
            await checkPush({
              lastPushActivity,
              projectId: project.id,
              projectDefaultBranch: project.default_branch,
              onFinish,
              projectSubscription: projectSubscription,
              onboardingType
            });
            return;
          }
        } catch (error) {
          if (
            error instanceof Error &&
            error.message === errorTypes.deployment
          ) {
            setNewProjectValues(currentState => ({
              ...currentState,
              errorType: errorTypes.deployment,
              state: projectCreationStates.isWaitingForPush
            }));
            return;
          }

          if (error instanceof Error && error.message === errorTypes.retry) {
            setNewProjectValues(currentState => ({
              ...currentState,
              errorType: errorTypes.retry,
              state: projectCreationStates.default
            }));
            return;
          }

          if (
            error instanceof Error &&
            error.message === errorTypes.provisioningFailure
          ) {
            setNewProjectValues(currentState => ({
              ...currentState,
              errorType: errorTypes.provisioningFailure,
              state: projectCreationStates.default
            }));
            return;
          }

          if (error instanceof Error && error.message === errorTypes.custom) {
            setNewProjectValues(currentState => ({
              ...currentState,
              errorType: errorTypes.custom,
              errorDetail: (error as { detail?: string }).detail,
              state: projectCreationStates.default
            }));
            return;
          }

          setNewProjectValues(currentState => ({
            ...currentState,
            errorType: errorTypes.redirect,
            state: projectCreationStates.default
          }));
        }
      };

      const onFinish = () => {
        clearInterval(checkSubscriptionInterval);
      };

      checkSubscriptionIntervalCallback({ onFinish });

      const checkSubscriptionInterval = setInterval(
        () => checkSubscriptionIntervalCallback({ onFinish }),
        10000
      );

      setTimeout(() => {
        clearInterval(checkSubscriptionInterval);
      }, 900000);
    },
    [
      hasStartedChecking,
      getSubscription,
      getProject,
      activeProjectSubscription,
      getActivities,
      checkPush,
      organizationName
    ]
  );

  async function createProject(
    data: SetupStepperContextType["setupStepperValues"] & {
      verificationType: VerificationTypes;
    },
    options?: {
      shouldSkipVerifyCheck?: boolean;
      isOnTrial?: boolean;
      isRetry?: boolean;
    }
  ) {
    const skipCheck = options?.shouldSkipVerifyCheck ?? false;
    if (!skipCheck && data.verificationType !== verificationTypes.none) {
      return navigate(`/${data.organization.name}/create-project/verify`, {
        replace: true
      });
    }

    setIsCreateProjectLoading(true);

    try {
      const subscription =
        data.onboardingType === onboardingTypes.migrate
          ? await createProjectFromGithub(data)
          : await safeClient(
              async () =>
                await client.createOrganizationSubscription({
                  organizationId: data.organization.value,
                  title: data.projectName,
                  defaultBranch: data.productionEnvironment,
                  region: data.region.id,
                  // There's a default on the client and has to be overwritten
                  plan: null,
                  ...(data.onboardingType === onboardingTypes.demo && {
                    optionsCustom: {
                      initialize: {
                        repository: intl.formatMessage({
                          id: "links.demo_project_git_url"
                        }),
                        profile: "demo"
                      }
                    }
                  })
                })
            ).then(res => (res.success ? res.response : undefined));
      const redirectURL = `/${data.organization.name}/create-project/setup/${
        data.onboardingType
      }/${(subscription.data?.id || subscription?.id) ?? "error"}`;
      if (options?.isRetry) {
        // We need to trigger a refresh on retry
        window.location.href = `${window.location.origin}${redirectURL}`;
      } else {
        navigate(redirectURL, { replace: true });
      }
      if (subscription.data?.id || subscription?.id) {
        checkSubscriptionStatus({
          projectId: subscription.data?.id || subscription?.id,
          organizationId: data.organization.value,
          onboardingType: data.onboardingType
        });
      }
    } catch {
      setNewProjectValues(currentState => ({
        ...currentState,
        errorType: errorTypes.retry
      }));
    }

    setIsCreateProjectLoading(false);
  }

  return {
    createProject,
    newProjectValues,
    isCreateProjectLoading,
    checkSubscriptionStatus,
    activities
  };
}

export function useStripePaymentType(
  organizationId: string,
  options?: { customApiKey: string }
) {
  const [paymentTypes, setPaymentTypes] = useState<
    Array<Record<string, string>>
  >([]);
  const [isPaymentTypeLoading, setIsPaymentTypeLoading] = useState(true);

  useEffect(() => {
    let isCanceled = false;

    const getAllowed = async () => {
      setIsPaymentTypeLoading(true);
      const allowedPaymentSources =
        await client.getOrganizationPaymentSourcesAllowed(organizationId);
      if (isCanceled) {
        return;
      }
      setPaymentTypes(allowedPaymentSources?.items);
      setIsPaymentTypeLoading(false);
    };

    getAllowed();
    return () => {
      isCanceled = true;
    };
  }, [organizationId]);

  const stripePromise = useMemo(() => {
    const apiKey =
      options?.customApiKey ||
      paymentTypes.find(paymentType => paymentType.id === "credit-card")
        ?.stripe_public_key;

    if (apiKey) return loadStripe(apiKey);
  }, [options?.customApiKey, paymentTypes]);

  return { paymentTypes, isPaymentTypeLoading, stripePromise };
}

export function useStripeAddCreditCard(
  organizationId: string,
  options?: { customClientSecret: string }
) {
  const me = useAppSelector(meSelector);
  const [isAddCreditCardLoading, setIsAddCreditCardLoading] = useState(false);
  const [addCreditCardError, setAddCreditCardError] = useState<string>();
  const elements = useElements();
  const stripe = useStripe();

  async function handleAddCreditCard() {
    setIsAddCreditCardLoading(true);

    const cardElement = elements?.getElement(CardElement);

    try {
      const secret =
        options?.customClientSecret ||
        (await client
          .createOrganizationPaymentSourceIntent(organizationId)
          .then(response => response.client_secret));

      const cardTokenId = await stripe!
        .confirmCardSetup(secret, {
          payment_method: {
            billing_details: {
              name: me?.display_name,
              email: me?.mail
            },
            card: cardElement!
          }
        })
        .then(response => {
          if (response?.error?.message) {
            throw response.error.message;
          }
          return response.setupIntent?.payment_method as string;
        });

      const organizationPaymentSource =
        await client.addOrganizationPaymentSource(
          organizationId,
          "credit-card",
          cardTokenId,
          me?.mail || "",
          false
        );
      return organizationPaymentSource;
    } catch (error) {
      logger(error);
      return setAddCreditCardError(
        typeof error === "string" ? error : "An error occurred"
      );
    } finally {
      setIsAddCreditCardLoading(false);
    }
  }

  return { handleAddCreditCard, isAddCreditCardLoading, addCreditCardError };
}
